<template>
  <tr
    v-show="row.expanded"
    class="table-row table-row--child order-item-details mt-0"
    :class="{'is-expanded': row.expanded}"
  >
    <td class="b2b-grid__cell block lg:table-cell lg:px-2 overflow-visible h-auto" :colspan="totalColumns">
      <template
        v-if="
          dataLoaded &&
            ((order.allocatedItems && order.allocatedItems.length) ||
              (order.notAllocatedItems && order.notAllocatedItems.length))
        "
      >
        <tabs :value="defaultTab">
          <tab-pane v-for="tab in tabs" :key="tab.id" :name="tab.id">
            <div slot="label" class>{{ tab.label }} ({{ order[tab.key].length }})</div>
            <table class="b2b-grid b2b-grid--collapse">
              <thead v-if="order[tab.key].length" class="b2b-grid__header">
                <tr>
                  <th class="b2b-grid__cell b2b-grid__head">
                    <!-- For Icons -->
                  </th>
                  <th
                    v-for="(column, index) in getColumns(tab.key)"
                    :key="column.key + index"
                    class="b2b-grid__cell b2b-grid__head"
                    :class="[column.cssClasses]"
                  >
                    <span v-html="column.label"></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-if="order[tab.key].length">
                  <tr v-for="(item, itemIndex) in order[tab.key]" :key="item.id + itemIndex" class="table-row">
                    <td class="b2b-grid__cell lg:table-cell whitespace-nowrap">
                      <div class="flex flex-nowrap">
                        <grid-icon v-if="item.isSplitWarehouse" :data="{value: 'call_split'}" />
                        <grid-note
                          v-show="item.lineComment || false"
                          :row-id="item.id"
                          :data="{
                            value: 'insert_drive_file',
                            content: item.lineComment,
                            editable: false,
                            cssClass: 'ml-1',
                          }"
                        ></grid-note>
                      </div>
                    </td>

                    <td
                      v-for="column in getColumns(tab.key)"
                      :key="`${item.id}-${column.key}`"
                      class="b2b-grid__cell lg:table-cell"
                      :class="[column.cssClasses]"
                    >
                      <div class="b2b-grid__label" v-html="column.label"></div>

                      <div class="b2b-grid__content">
                        <!-- Cancel Date cell -->
                        <div v-if="column.key === 'cancelDate'">
                          <i v-if="item.showCancelFlag" class="b2b-grid__icon material-icons text-warning">warning</i>
                          <span v-html="item[column.key]"></span>
                        </div>
                        <!-- All other cells -->
                        <span v-if="column.key !== 'cancelDate'" v-html="item[column.key]"></span>

                        <div v-if="column.key === 'etaWatch'">
                          <div :id="`p1-tooltip-${itemIndex}`" v-tippy-html>{{ p1WatchMessage }}</div>
                          <form id="etaWatchForm"></form>
                          <button
                            v-tippy="{
                              html: `#p1-tooltip-${itemIndex}`,
                              interactive: true,
                              reactive: true,
                              trigger: item.p1flag ? 'mouseenter focus' : 'manual',
                              flip: true,
                              sticky: true,
                            }"
                            :disabled="isProcessing"
                            class="b2b-grid__stock-watch__indicator"
                            :class="[{'text-success': item.etaToggle === true}, {disabled: item.p1flag === true}]"
                            @click="updateEtaWatch(item, itemIndex)"
                          >
                            <i class="b2b-grid__stock-watch__icon material-icons">{{
                              item.etaToggle === true ? 'visibility' : 'visibility_off'
                            }}</i>
                            <span class="b2b-grid__stock-watch__label b2b-grid__stock-watch__label font-medium">{{
                              getBaseProperty('watch')
                            }}</span>
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
                <tr v-if="!order[tab.key].length">
                  <td class="bg-gray-05 p-3 text-center font-medium" :colspan="totalColumns">
                    <span v-if="tab.key === 'allocatedItems'" key="emptyText">{{ bp('noAllocatedItems') }}</span>
                    <span v-else key="emptyText">{{ bp('noBackorderedItems') }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <section v-if="order[tab.key].length" class="mb-2">
              <div class="grid-x grid-margin-x">
                <div class="cell medium-6 large-8">
                  <!-- Pagination -->
                </div>

                <div v-if="row.subTotals && row.subTotals[tab.subtotalKey]" class="cell medium-6 large-4">
                  <ul v-if="!consumerFriendlyMode" class="block-list text-md border-b border-gray-10">
                    <li class="block-list__item text-base font-medium">
                      <span>{{ bp('shipmentTotal') }}</span>
                      <span v-if="row.subTotals[tab.subtotalKey]['price']">{{
                        row.subTotals[tab.subtotalKey]['price']['formattedValue']
                      }}</span>
                    </li>
                  </ul>
                  <div class="buttons buttons--right-for-md mt-2">
                    <trek-link button secondary small :href="grid.linkManageAllocatedItems">
                      <span v-html="bp('manageAllocatedItems')"></span>
                    </trek-link>
                  </div>
                </div>
              </div>
            </section>
          </tab-pane>
        </tabs>
      </template>
    </td>
  </tr>
</template>

<script>
import axios from 'axios';
import find from 'lodash/find';
import {mapGetters} from 'vuex';
import TrekLink from '@/components/TrekLink';
import Tabs from '@/components/tabs/Tabs';
import TabPane from '@/components/tabs/TabPane';
import groupBy from 'lodash/groupBy';
import GridNote from '@/components/grid/GridNote';
import GridIcon from '@/components/grid/GridIcon';

export default {
  components: {TrekLink, Tabs, TabPane, GridNote, GridIcon},
  inject: ['grid'],

  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    // needs to be refactored to make eslint happy.
    /* eslint-disable */
    row: [Object, Array],
    rowId: [String, Number],
    /* eslint-enable */
    rowKey: {
      type: String,
      default: 'id',
    },
  },

  data() {
    return {
      dataLoaded: false,
      isProcessing: false,
    };
  },

  computed: {
    tabs() {
      return [
        {
          id: `${this.order.id}-tab-allocated`,
          label: this.bp('allocated'),
          key: 'allocatedItems',
          subtotalKey: 'ALLOCATED',
        },
        {
          id: `${this.order.id}-tab-notallocated`,
          label: this.bp('backordered'),
          key: 'notAllocatedItems',
          subtotalKey: 'BACKORDERED',
        },
      ];
    },

    order() {
      return this.row;
    },

    defaultTab() {
      if (this.order.allocatedItems.length) {
        return `${this.row.id}-tab-allocated`;
      } else {
        return `${this.row.id}-tab-notallocated`;
      }
    },

    totalColumns() {
      // We have one statically created column for the icons
      return this.grid.columns.length + 1;
    },

    totalBackorderedItems() {
      if (!this.order.allocatedItems && !this.order.notAllocatedItems) {
        return 0;
      }

      return this.order.allocatedItems.length + this.order.notAllocatedItems.length;
    },

    p1WatchMessage() {
      return this.bp('p1Watch');
    },
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },

  watch: {
    row(newRow, oldRow) {
      if (newRow.id !== oldRow.id && (newRow.hasBackorderedItems || newRow.hasAllocatedItems)) {
        this.$nextTick(() => {
          this.fetchOrderDetails();
        });
      }
    },
  },

  created() {
    if (this.row.hasBackorderedItems || this.row.hasAllocatedItems) {
      this.fetchOrderDetails();
    }
  },

  methods: {
    getBaseProperty(name) {
      let baseProperty = find(this.baseProperties, (baseProperty) => baseProperty.name === name);
      return baseProperty ? baseProperty.label : '';
    },

    getColumns(tabKey) {
      // The "Backordered" shouldn't have the Allocated or Cancel Date columns
      return this.grid.childColumns.filter((column) => this.filterColumns(column, tabKey));
    },

    filterColumns(column, tabKey) {
      if (tabKey === 'notAllocatedItems') {
        if (column.key === 'quantityAllocatedOrShipped' || column.key === 'cancelDate') return false;
      } else if (column.key === 'etaDate' || column.key === 'etaWatch') {
        return false;
      }
      if (this.consumerFriendlyMode && (column.key === 'unitPrice' || column.key == 'lineSubtotal')) return false;
      return true;
    },

    fetchOrderDetails() {
      this.dataLoaded = false;

      const {orderNumber, orderType, orderCompany} = this.row;

      axios.get(`/account/order/json/v2/${orderNumber}?company=${orderCompany}&ordertype=${orderType}`).then(
        ({
          data: {
            data: {entries = {}, subTotals = {}},
          },
        }) => {
          let backorderedEntries = [];
          let allocatedEntries = [];

          let groupedObj = groupBy(entries, 'groupingType');

          for (const key in groupedObj) {
            if (key == 'BACKORDERED') {
              backorderedEntries = backorderedEntries.concat(groupedObj[key]);
            }
            if (key == 'ALLOCATED') {
              allocatedEntries = allocatedEntries.concat(groupedObj[key]);
            }
          }

          if (backorderedEntries) {
            backorderedEntries.forEach((element, index) => {
              element.lineSubtotal = element.totalPrice.formattedValue;
              element.unitPrice = element.basePrice.formattedValue;
              element.orderLineNumber = index + 1;
              if (element.etaDate && this.bp(element.etaDate)) {
                element.etaDate = this.bp(element.etaDate);
              }
            });
          }

          if (allocatedEntries) {
            allocatedEntries.forEach(function(element) {
              element.lineSubtotal = element.totalPrice.formattedValue;
              element.unitPrice = element.basePrice.formattedValue;
            });
          }

          this.$set(this.order, 'allocatedItems', allocatedEntries);
          this.$set(this.order, 'notAllocatedItems', backorderedEntries);
          this.$set(this.order, 'subTotals', subTotals);

          this.dataLoaded = true;
        }
      );
    },

    updateEtaWatch(item, itemIndex) {
      const token = $('[name="CSRFToken"]').val();
      const {sku, warehouse, orderLineNumber} = item;
      const rowItem = this.row.notAllocatedItems[itemIndex];
      const payload = {
        sku,
        warehouse,
        orderLineNumber,
        orderNumber: this.row.orderNumber,
        orderType: this.row.orderType,
        orderCompany: this.row.orderCompany,
      };

      this.isProcessing = true;
      if (item.p1flag === true) {
        // P1 items cannot change watch status
      } else if (item.etaWatchId && item.etaToggle === true) {
        // If the item already has an ETA Watch, we send a delete request
        axios
          .delete(`/my-account/etawatch/${item.etaWatchId}`, payload)
          .then(() => {
            rowItem.etaToggle = false;
            rowItem.etaWatchId = null;
            this.isProcessing = false;
          })
          .catch((error) => console.log(error));
      } else {
        // If the item has no ETA Watch then send a POST request
        axios
          .post(`/my-account/etawatch?CSRFToken=${token}`, payload)
          .then(({data}) => {
            rowItem.etaToggle = data.data.success;
            rowItem.etaWatchId = data.data.id;
            this.isProcessing = false;
          })
          .catch((error) => console.log(error));
      }
    },
  },
};
</script>
